import { tempoBegrenzen } from './rennen';
import {
  MOVE_SEND_RATE_HZ,
  PLAYER_SPEED_TILES_PER_S,
  directionFromYaw,
  yawDelta,
  yawFromDirection,
  type CollisionGrid,
  type Direction,
  type PositionUpdate,
  type SpawnPoint,
} from '@etown/shared';
import { CORRECTION_LERP, CORRECTION_THRESHOLD_TILES } from '../lib/constants';

const SEND_INTERVAL_MS = 1000 / MOVE_SEND_RATE_HZ;
/**
 * Ab dieser Drehung (etwa 2 Grad) wird eine Bewegung gesendet, auch wenn der
 * Spieler steht — sonst saehe ein 2D-Nutzer das Umschauen in der Ego-Ansicht
 * nie. Die Sendrate bleibt trotzdem bei MOVE_SEND_RATE_HZ gedeckelt.
 */
const YAW_SEND_THRESHOLD_RAD = 0.035;
/**
 * Wie viele unbestaetigte Bewegungen normal sind. Bei 15 Hz sind das gut
 * acht Zehntelsekunden Laufzeit — grosszuegig genug, dass eine langsame
 * Verbindung nicht faelschlich als Desync gilt.
 */
const UNBESTAETIGT_MAX = 12;
/** Obergrenze des Verlaufs, damit er nicht unbegrenzt waechst. */
const VERLAUF_MAX = 120;

export type SendMove = (move: { x: number; y: number; direction: Direction; yaw?: number; seq: number }) => void;

/**
 * Lokaler Avatar: bewegt sich sofort (null gefuehlte Latenz) mit derselben
 * Kollisionslogik, die auch der Server prueft. Positionen gehen mit 15 Hz
 * und laufender Sequenznummer an den Server; weicht dessen autoritative
 * Position deutlich ab (Korrektur), gleiten wir weich dorthin zurueck.
 */
export class LocalPlayer {
  x: number;
  y: number;
  direction: Direction;
  /** Blickwinkel der Ego-Ansicht; null = Draufsicht, dann zaehlt nur direction. */
  yaw: number | null = null;
  moving = false;
  private seq = 0;
  /**
   * Zeitpunkt der letzten gesendeten Bewegung auf der *echten* Uhr.
   *
   * Bewusst nicht als Summe der Simulationsschritte: die Schleife holt nach
   * einem langen Frame mehrere Schritte auf einmal nach, und die tragen
   * zusammen leicht mehr als ein Sendeintervall. Gezaehlt in Simulationszeit
   * feuerten dann zwei oder drei Nachrichten in derselben Millisekunde — der
   * Server sieht zwischen ihnen keine Zeit vergehen, haelt die zweite fuer zu
   * weit und verwirft sie. Das Ergebnis fuehlt sich an wie eine unsichtbare
   * Wand.
   */
  private lastSentAtMs = Number.NEGATIVE_INFINITY;
  private lastSent = {
    x: Number.NaN,
    y: Number.NaN,
    direction: 'down' as Direction,
    yaw: null as number | null,
  };
  private correction: { x: number; y: number } | null = null;
  /**
   * Die zuletzt gesendeten Bewegungen mit ihrer Nummer.
   *
   * Ohne sie laesst sich eine Server-Antwort nicht einordnen: sie ist immer
   * unterwegs gewesen und beschreibt einen Stand von vor einer Laufzeit.
   * Verglichen mit dem *jetzigen* Ort sieht jede gesunde Verbindung nach
   * Abweichung aus — bei fuenf Kacheln je Sekunde sind 150 ms schon eine
   * Dreiviertelkachel.
   */
  private verlauf: Array<{ seq: number; x: number; y: number }> = [];
  /** Praediktive Zutrittspruefung (verbotene Zonen); null = ueberall erlaubt. */
  private isAllowed: ((x: number, y: number) => boolean) | null = null;
  /**
   * Sitzt der Avatar auf einem Stuhl? Dann bleibt der Fusspunkt stehen, wo er
   * ist — der Blick bleibt frei, nur die Beine machen Pause.
   */
  private sitzend = false;

  constructor(
    spawn: Pick<SpawnPoint, 'x' | 'y' | 'dir'>,
    private collision: CollisionGrid,
    private readonly sendMove: SendMove,
  ) {
    this.x = spawn.x;
    this.y = spawn.y;
    this.direction = spawn.dir;
  }

  /** Nach map:updated: neues Kollisionsgrid uebernehmen. */
  setCollision(collision: CollisionGrid): void {
    this.collision = collision;
  }

  /** Zutrittspruefung setzen (praediktiv, damit es an der Grenze nicht ruckelt). */
  setAccessCheck(fn: ((x: number, y: number) => boolean) | null): void {
    this.isAllowed = fn;
  }

  /**
   * Blickwinkel der Ego-Ansicht setzen (null beim Wechsel zurueck in die
   * Draufsicht). Die Vier-Richtungs-Pose folgt daraus, damit 2D-Nutzer die
   * Drehung sehen.
   */
  setYaw(yaw: number | null): void {
    this.yaw = yaw;
    if (yaw !== null) this.direction = directionFromYaw(yaw);
  }

  get sitzt(): boolean {
    return this.sitzend;
  }

  /**
   * Auf einen Stuhl setzen; (x, y) ist dessen Kachelmitte.
   *
   * Der Fusspunkt springt dorthin — bis zu anderthalb Kacheln weit, und das
   * darf er: der Server fuellt sein Bewegungsbudget im Stehen bis zum Deckel
   * von acht Kacheln auf. Eine ausstehende Korrektur wird verworfen, sie
   * wuerde uns sonst gleich wieder vom Stuhl ziehen.
   */
  hinsetzen(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.correction = null;
    this.sitzend = true;
  }

  aufstehen(): void {
    this.sitzend = false;
  }

  /** Startblickrichtung beim Wechsel in die Ego-Ansicht. */
  yawOrFacing(): number {
    return this.yaw ?? yawFromDirection(this.direction);
  }

  /**
   * @param dtMs Simulationsschritt (fest), bestimmt die zurueckgelegte Strecke
   * @param nowMs echte Uhr, drosselt das Senden
   */
  update(dtMs: number, input: { x: number; y: number }, nowMs: number): void {
    const dt = dtMs / 1000;

    // Ausstehende Server-Korrektur weich einarbeiten
    if (this.correction) {
      this.x += (this.correction.x - this.x) * CORRECTION_LERP;
      this.y += (this.correction.y - this.y) * CORRECTION_LERP;
      const remaining = Math.hypot(this.correction.x - this.x, this.correction.y - this.y);
      if (remaining < 0.02) this.correction = null;
    }

    // Wer sitzt, geht nicht — die Eingabe laeuft ins Leere, bis er aufsteht
    const bewegung = this.sitzend ? { x: 0, y: 0 } : tempoBegrenzen(input);
    this.moving = bewegung.x !== 0 || bewegung.y !== 0;
    if (this.moving) {
      const step = PLAYER_SPEED_TILES_PER_S * dt;
      const next = this.collision.moveWithSlide(this.x, this.y, bewegung.x * step, bewegung.y * step);
      let nx = next.x;
      let ny = next.y;
      // Verbotene Zonen wie Waende behandeln — achsengetrennt gleiten
      if (this.isAllowed && !this.isAllowed(nx, ny)) {
        const xOk = this.isAllowed(nx, this.y);
        const yOk = this.isAllowed(this.x, ny);
        if (xOk && !yOk) ny = this.y;
        else if (yOk && !xOk) nx = this.x;
        else { nx = this.x; ny = this.y; }
      }
      this.x = nx;
      this.y = ny;
      // In der Ego-Ansicht bestimmt der Blick die Pose, nicht die Laufrichtung
      if (this.yaw === null) this.direction = directionFromVector(bewegung.x, bewegung.y, this.direction);
    }

    // Throttled senden — nur wenn sich tatsaechlich etwas geaendert hat.
    // Gemessen an der echten Uhr, damit mehrere Nachholschritte eines Frames
    // zusammen hoechstens eine Nachricht ergeben.
    if (nowMs - this.lastSentAtMs >= SEND_INTERVAL_MS) {
      this.lastSentAtMs = nowMs;
      const changed =
        Math.abs(this.x - this.lastSent.x) > 0.001 ||
        Math.abs(this.y - this.lastSent.y) > 0.001 ||
        this.direction !== this.lastSent.direction ||
        this.turnedNoticeably();
      if (changed) {
        this.seq += 1;
        this.sendMove({
          x: this.x,
          y: this.y,
          direction: this.direction,
          ...(this.yaw === null ? {} : { yaw: this.yaw }),
          seq: this.seq,
        });
        this.lastSent = { x: this.x, y: this.y, direction: this.direction, yaw: this.yaw };
        this.verlauf.push({ seq: this.seq, x: this.x, y: this.y });
        if (this.verlauf.length > VERLAUF_MAX) this.verlauf.shift();
      }
    }
  }

  /** Hat sich der Blick seit dem letzten Senden spuerbar gedreht? */
  private turnedNoticeably(): boolean {
    if (this.yaw === null) return this.lastSent.yaw !== null;
    if (this.lastSent.yaw === null) return true;
    return Math.abs(yawDelta(this.lastSent.yaw, this.yaw)) > YAW_SEND_THRESHOLD_RAD;
  }

  /**
   * Echo des Servers fuer den eigenen Avatar: Weicht die autoritative
   * Position spuerbar ab (abgelehnte Bewegung, Lag-Spike), Korrektur starten.
   */
  applyServerUpdate(update: PositionUpdate): void {
    // Die Antwort gehoert zu einer bestimmten gesendeten Bewegung. Verglichen
    // wird deshalb mit dem Ort von damals, nicht mit dem von jetzt.
    const damals = this.verlauf.find((e) => e.seq === update.seq);
    this.verlauf = this.verlauf.filter((e) => e.seq > update.seq);

    if (damals) {
      const fx = update.x - damals.x;
      const fy = update.y - damals.y;
      // Weicht der Server an derselben Stelle ab, hat er wirklich etwas
      // anders entschieden. Der Fehler wird als Versatz auf den aktuellen
      // Ort gelegt — was seither vorhergesagt wurde, bleibt erhalten.
      if (Math.hypot(fx, fy) > CORRECTION_THRESHOLD_TILES) {
        this.correction = { x: this.x + fx, y: this.y + fy };
      }
      return;
    }

    // Leerer Verlauf heisst: nichts ist unterwegs, der Server kann nur
    // etwas melden, das nicht von uns kam — ein Zonen-Rauswurf, ein
    // Spawn-Versatz nach einer Map-Aenderung. Dann ist seine Position die
    // Wahrheit, und der Vergleich mit dem jetzigen Ort ist ausnahmsweise
    // richtig, weil es keine eigene Vorhersage gibt, die er verfaelschen
    // koennte. Ohne diesen Zweig staende ein ruhender Spieler fuer die
    // anderen am Spawn und saehe sich selbst noch im Raum.
    if (this.verlauf.length === 0) {
      if (Math.hypot(update.x - this.x, update.y - this.y) > CORRECTION_THRESHOLD_TILES) {
        this.correction = { x: update.x, y: update.y };
      }
      return;
    }

    // Keine passende Nummer: der Server haengt seit vielen Nachrichten auf
    // demselben Stand — er hat sie also abgelehnt oder verloren. Dann ist
    // seine Position die Wahrheit.
    if (this.verlauf.length > UNBESTAETIGT_MAX) {
      this.correction = { x: update.x, y: update.y };
      this.verlauf = [];
    }
  }

  /** Spawn-Reset (z. B. wenn die Map-Aenderung den Standort ungueltig macht). */
  teleport(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.correction = null;
    this.verlauf = [];
  }
}

function directionFromVector(x: number, y: number, fallback: Direction): Direction {
  if (Math.abs(x) > Math.abs(y)) return x > 0 ? 'right' : 'left';
  if (y !== 0) return y > 0 ? 'down' : 'up';
  return fallback;
}
